/**
 * World — Verlet physics container for up to MAX_BLOBS dynamic bodies.
 *
 * Coordinate convention: pixels, origin at canvas centre, +y up.
 *
 * Reset = empty world. Blobs are added via `addBlob(...)` (typically by a
 * mouse click). When the count reaches MAX_BLOBS the oldest is evicted FIFO.
 *
 * Forces (per step):
 *   1. Long-range gravity-like attraction between every pair (§5.1 in SPEC),
 *      scaled by both bodies' spawn-influence (cubic ramp on spawnT).
 *   2. Short-range surface tension (applySurfaceTension) inside the contact
 *      band around tangent — what makes touching blobs stick.
 *   3. Linear drag.
 *
 * Constraints (after integration):
 *   * `constrainToContainer` clamps each body inside the rectangular frame,
 *     accounting for the line stroke radius. On contact the wall-normal
 *     velocity component is multiplied by -0.4 (gentle damping, NOT a hard
 *     bounce). The shader does its own SDF-level smooth-max with the frame
 *     so the visual flattening at walls is mathematical, not animated.
 *
 * Spawn animation (§5.5):
 *   * spawnT advances dt/spawnDuration each frame. Rendered radius is
 *     targetRadius * easeOutCubic(spawnT). spawnInfluence (= spawnT^3) gates
 *     gravity contribution so newborns settle in.
 *
 * Vibration:
 *   * vibEnergy is set to a small positive value at spawn time and decays
 *     exponentially with τ = params.vibrationDecayTau. The shader uses it
 *     to modulate the slow-ellipse pulseAmp + rotRate so fresh blobs
 *     "breathe" more vigorously than settled ones.
 */
import type { BlobColor } from '../types.js';
import { BlobBody, DEFAULT_LIFETIME_SEC } from './blob.js';
import { applySurfaceTension, computeSminK } from './surface-tension.js';

export const MAX_BLOBS = 10;

export interface PhysicsParams {
  gravity: number;
  attractionMinR: number;
  drag: number;
  /** τ (seconds) for the spawn-vibration exponential decay. */
  vibrationDecayTau: number;
}

export interface WorldBounds {
  width: number;
  height: number;
}

export interface RenderState {
  /** Active blob count, 0..MAX_BLOBS. */
  blobCount: number;
  /** Length MAX_BLOBS*2: [x0,y0, ..., xN,yN]. Unused slots are zero. */
  positions: Float32Array;
  /** Length MAX_BLOBS: current rendered radius (= targetRadius * easeOutCubic(spawnT)). */
  radii: Float32Array;
  /** Length MAX_BLOBS. */
  spawnT: Float32Array;
  /** Length MAX_BLOBS. */
  warpSeeds: Float32Array;
  /** Length MAX_BLOBS — per-blob smoothness for the chain smin in the shader.
   *  Slot 0's value is unused (it is the seed of the chain). */
  sminK: Float32Array;
  /** Length MAX_BLOBS — modulates the slow-ellipse pulseAmp / rotRate. */
  vibEnergy: Float32Array;
  /** Bumps when the blob list mutates (add/evict). Used by app.ts to know
   *  when to rebuild the hotspot payload. */
  version: number;
}

/** Initial vibration energy per spawn. Decays via vibrationDecayTau. */
const SPAWN_VIB_ENERGY = 0.4;
/** Wall-collision velocity damping factor (1.0 = elastic, 0 = absorb).
 *  0.1 = barely visible bounce. The SDF-level smooth-max in blob.frag
 *  flattens the body against the wall regardless of this value, so a tiny
 *  rebound is fine and the wall reads as soft contact rather than a bumper. */
const CONTAINER_VELOCITY_DAMP = 0.1;

export interface AddBlobOpts {
  position: [number, number];
  color: BlobColor;
  targetRadius: number;
  warpSeed: number;
  spawnDuration?: number;
  mass?: number;
  /** Seconds before this blob fades out (default DEFAULT_LIFETIME_SEC). */
  lifetime?: number;
}

export class World {
  blobs: BlobBody[] = [];
  bounds: WorldBounds;
  params: PhysicsParams;
  rng: () => number;

  private lastDt = 1 / 60;
  private version = 0;

  // Render-state scratch arrays. Allocated once at MAX_BLOBS and zeroed each frame.
  private renderPositions = new Float32Array(MAX_BLOBS * 2);
  private renderRadii = new Float32Array(MAX_BLOBS);
  private renderSpawnT = new Float32Array(MAX_BLOBS);
  private renderWarpSeeds = new Float32Array(MAX_BLOBS);
  private renderSminK = new Float32Array(MAX_BLOBS);
  private renderVibEnergy = new Float32Array(MAX_BLOBS);

  /**
   * Optional outer-frame inset, in pixels, accounted for during the container
   * constraint. Equals the blob's outline half-width so a stroked blob still
   * fits entirely inside the export frame.
   */
  lineWidthHalfPx = 0;

  constructor(params?: PhysicsParams, bounds?: WorldBounds, rng?: () => number) {
    this.params = params
      ? { ...params }
      : {
          gravity: 0.04,
          attractionMinR: 54,
          drag: 0.0015,
          vibrationDecayTau: 4.0,
        };
    this.bounds = bounds ? { ...bounds } : { width: 1920, height: 1080 };
    this.rng = rng ?? defaultRng();
  }

  /** Empty the world. Called by Reset (start-over mode). */
  reset(): void {
    this.blobs = [];
    this.bumpVersion();
  }

  /** Add a new blob. Evicts the oldest entry FIFO if the world is full.
   *
   *  Lifetime rules (see SPEC §水滴ライフタイム):
   *  - If the world currently has exactly 1 active blob when this is called,
   *    that lone survivor is revived (age = 0, lifetime = REVIVE_LIFETIME_SEC,
   *    radius restored). The revive happens BEFORE the FIFO eviction check —
   *    at length 1 we are nowhere near MAX_BLOBS so the order doesn't matter
   *    semantically, but conceptually we want to act on the existing blob
   *    before any list mutation.
   *  - The new blob always uses the default (or opts.lifetime) lifetime,
   *    independent of the revive path. */
  addBlob(opts: AddBlobOpts): BlobBody {
    // Re-extend the lone survivor's life when a new droplet joins it.
    if (this.blobs.length === 1) {
      this.blobs[0]?.revive();
    }

    if (this.blobs.length >= MAX_BLOBS) {
      this.blobs.shift();
    }

    const body = new BlobBody({
      id: hexId(this.rng),
      position: opts.position,
      targetRadius: opts.targetRadius,
      spawnT: 0,
      spawnDuration: opts.spawnDuration ?? 0.6,
      mass: opts.mass ?? 1,
      color: opts.color,
      warpSeed: opts.warpSeed,
      vibEnergy: SPAWN_VIB_ENERGY,
      lifetime: opts.lifetime ?? DEFAULT_LIFETIME_SEC,
    });
    this.blobs.push(body);
    this.bumpVersion();
    return body;
  }

  setBounds(bounds: WorldBounds): void {
    this.bounds = { ...bounds };
    const halfW = bounds.width / 2;
    const halfH = bounds.height / 2;
    for (const blob of this.blobs) {
      const r = blob.radius + this.lineWidthHalfPx;
      const minX = -halfW + r;
      const maxX = halfW - r;
      const minY = -halfH + r;
      const maxY = halfH - r;
      const cx = clamp(blob.position[0], minX, maxX);
      const cy = clamp(blob.position[1], minY, maxY);
      if (cx !== blob.position[0] || cy !== blob.position[1]) {
        blob.setPosition(cx, cy);
      }
    }
  }

  step(dt: number): void {
    if (dt <= 0) return;
    this.lastDt = dt;

    // "Last droplet stays" rule — when only one blob is active its age clock
    // freezes (and so does any in-progress shrink). Captured before any other
    // mutation in this step so all sub-passes see the same value.
    const isLast = this.blobs.length === 1;

    for (const blob of this.blobs) blob.setReferenceDt(dt);

    // 1. Advance spawn animations.
    for (const blob of this.blobs) blob.advanceSpawn(dt);

    // 1b. Advance lifetime / shrink-fade. tickAge rewrites targetRadius in
    //     place; the spawn-ramp `radius` getter sees the post-shrink value
    //     so the two effects compose multiplicatively.
    for (const blob of this.blobs) blob.tickAge(dt, isLast);

    // 2. Long-range gravity (cubic-spawn-gated).
    if (this.blobs.length >= 2) this.applyAttraction();

    // 3. Short-range surface tension (Lennard-Jones-like spring + damping).
    //    Spring is explicit (applyForce); damping is semi-implicit (setVelocity).
    const shortEdge = Math.min(this.bounds.width, this.bounds.height);
    applySurfaceTension(this.blobs, shortEdge, dt);

    // 4. Linear drag.
    this.applyDrag();

    // 5. Verlet step.
    for (const blob of this.blobs) blob.integrate(dt);

    // 6. Container constraint (positional clamp + gentle velocity damp).
    this.constrainToContainer();

    // 7. Vibration energy decay.
    this.decayVibration(dt);

    // 8. Reap expired blobs. Defensive `!isLast` guard: if the world had
    //    exactly one blob entering this step, its age was frozen and it
    //    cannot be expired here — but we double-check anyway so a future
    //    refactor that breaks the freeze invariant doesn't silently start
    //    deleting the lone survivor.
    if (!isLast) {
      let removed = false;
      for (let i = this.blobs.length - 1; i >= 0; i--) {
        if (this.blobs[i]?.isExpired) {
          this.blobs.splice(i, 1);
          removed = true;
        }
      }
      if (removed) this.bumpVersion();
    }
  }

  private applyAttraction(): void {
    const minR = this.params.attractionMinR;
    const G = this.params.gravity;
    for (let i = 0; i < this.blobs.length; i++) {
      const a = this.blobs[i];
      if (!a) continue;
      const aInfl = a.spawnInfluence;
      for (let j = i + 1; j < this.blobs.length; j++) {
        const b = this.blobs[j];
        if (!b) continue;
        const bInfl = b.spawnInfluence;
        const scale = aInfl * bInfl;
        if (scale === 0) continue;
        const dx = b.position[0] - a.position[0];
        const dy = b.position[1] - a.position[1];
        const rSq = dx * dx + dy * dy;
        const rEffSq = Math.max(rSq, minR * minR);
        const rEff = Math.sqrt(rEffSq);
        if (rEff === 0) continue;
        const fMag = (G * a.mass * b.mass * scale) / rEffSq;
        const ux = dx / rEff;
        const uy = dy / rEff;
        a.applyForce(fMag * ux, fMag * uy);
        b.applyForce(-fMag * ux, -fMag * uy);
      }
    }
  }

  private applyDrag(): void {
    const k = this.params.drag * 60;
    if (k === 0) return;
    for (const blob of this.blobs) {
      const v = blob.getVelocity();
      blob.applyForce(-k * blob.mass * v[0], -k * blob.mass * v[1]);
    }
  }

  /**
   * Hard rectangular constraint (no viscoelastic accumulator). Each blob
   * is clamped inside the frame minus its outline half-width; if it was
   * moving into a wall, the wall-normal component of velocity is reflected
   * with CONTAINER_VELOCITY_DAMP factor (gentle loss, not a hard bounce).
   *
   * Bug fix: position changes always go through `setPosition`, which
   * preserves the Verlet implicit velocity (`prevPosition` is updated to
   * keep `position − prevPosition` the same). Direct `position[0] = …`
   * was breaking that invariant — when a spawning blob's growing radius
   * pushed it inward each frame, `prevPosition` stayed at the original
   * spawn point and the implicit velocity exploded into a 1500 px/s
   * inward kick. With `setPosition` the implicit velocity stays at the
   * pre-clamp value (zero, for a fresh spawn), and only the explicit
   * reflection branch (via `setVelocity`) overrides it.
   */
  private constrainToContainer(): void {
    const halfW = this.bounds.width / 2;
    const halfH = this.bounds.height / 2;
    const lineHalf = this.lineWidthHalfPx;

    for (let i = 0; i < this.blobs.length; i++) {
      const blob = this.blobs[i];
      if (!blob) continue;
      const r = blob.radius + lineHalf;
      const minX = -halfW + r;
      const maxX = halfW - r;
      const minY = -halfH + r;
      const maxY = halfH - r;

      const oldX = blob.position[0];
      const oldY = blob.position[1];
      let newX = oldX;
      let newY = oldY;
      let [vx, vy] = blob.getVelocity();
      let touched = false;

      if (newX < minX) {
        newX = minX;
        if (vx < 0) {
          vx = -vx * CONTAINER_VELOCITY_DAMP;
          touched = true;
        }
      } else if (newX > maxX) {
        newX = maxX;
        if (vx > 0) {
          vx = -vx * CONTAINER_VELOCITY_DAMP;
          touched = true;
        }
      }

      if (newY < minY) {
        newY = minY;
        if (vy < 0) {
          vy = -vy * CONTAINER_VELOCITY_DAMP;
          touched = true;
        }
      } else if (newY > maxY) {
        newY = maxY;
        if (vy > 0) {
          vy = -vy * CONTAINER_VELOCITY_DAMP;
          touched = true;
        }
      }

      const positionChanged = newX !== oldX || newY !== oldY;
      if (positionChanged) {
        blob.setPosition(newX, newY); // preserves implicit velocity
      }
      if (touched) {
        blob.setVelocity([vx, vy]); // overrides with damped reflection
      }
    }
  }

  /** Exponential decay of per-blob vibEnergy with τ = vibrationDecayTau. */
  private decayVibration(dt: number): void {
    const tau = Math.max(0.05, this.params.vibrationDecayTau);
    const decay = Math.exp(-dt / tau);
    for (const blob of this.blobs) {
      blob.vibEnergy *= decay;
      if (blob.vibEnergy < 1e-3) blob.vibEnergy = 0;
    }
  }

  applyMouseDrag(blobIndex: number, mouseDelta: [number, number]): void {
    const blob = this.blobs[blobIndex];
    if (!blob) return;
    blob.setVelocity([mouseDelta[0] * 0.6, mouseDelta[1] * 0.6]);

    const halfW = this.bounds.width / 2;
    const halfH = this.bounds.height / 2;
    const r = blob.radius + this.lineWidthHalfPx;
    const cx = clamp(blob.position[0], -halfW + r, halfW - r);
    const cy = clamp(blob.position[1], -halfH + r, halfH - r);
    if (cx !== blob.position[0] || cy !== blob.position[1]) {
      blob.setPosition(cx, cy);
    }
  }

  /** Hit-test in world space. Returns blob index or null. */
  pickBlob(mouseWorldPos: [number, number]): number | null {
    let best: number | null = null;
    let bestDistSq = Infinity;
    for (let i = 0; i < this.blobs.length; i++) {
      const blob = this.blobs[i];
      if (!blob) continue;
      const r = blob.radius;
      if (r <= 0) continue;
      const dx = mouseWorldPos[0] - blob.position[0];
      const dy = mouseWorldPos[1] - blob.position[1];
      const dSq = dx * dx + dy * dy;
      if (dSq <= r * r && dSq < bestDistSq) {
        bestDistSq = dSq;
        best = i;
      }
    }
    return best;
  }

  /** Find a blob by id. Used to follow a body across reorderings. */
  findBlobIndexById(id: string): number | null {
    for (let i = 0; i < this.blobs.length; i++) {
      if (this.blobs[i]?.id === id) return i;
    }
    return null;
  }

  /** Snapshot the world for the renderer. All Float32Arrays are MAX_BLOBS-aligned. */
  getRenderState(): RenderState {
    const blobCount = Math.min(this.blobs.length, MAX_BLOBS);

    this.renderPositions.fill(0);
    this.renderRadii.fill(0);
    this.renderSpawnT.fill(0);
    this.renderWarpSeeds.fill(0);
    this.renderSminK.fill(0);
    this.renderVibEnergy.fill(0);

    for (let i = 0; i < blobCount; i++) {
      const blob = this.blobs[i];
      if (!blob) continue;
      this.renderPositions[i * 2] = blob.position[0];
      this.renderPositions[i * 2 + 1] = blob.position[1];
      this.renderRadii[i] = blob.radius;
      this.renderSpawnT[i] = blob.spawnT;
      this.renderWarpSeeds[i] = blob.warpSeed;
      this.renderVibEnergy[i] = blob.vibEnergy;
    }

    for (let i = 0; i < blobCount; i++) {
      const a = this.blobs[i];
      if (!a) continue;
      let best = 0;
      for (let j = 0; j < blobCount; j++) {
        if (j === i) continue;
        const b = this.blobs[j];
        if (!b) continue;
        const k = computeSminK(a, b);
        if (k > best) best = k;
      }
      this.renderSminK[i] = best;
    }

    return {
      blobCount,
      positions: this.renderPositions,
      radii: this.renderRadii,
      spawnT: this.renderSpawnT,
      warpSeeds: this.renderWarpSeeds,
      sminK: this.renderSminK,
      vibEnergy: this.renderVibEnergy,
      version: this.version,
    };
  }

  getLastDt(): number {
    return this.lastDt;
  }

  /** Internal version counter — bump on any blob-list mutation. */
  bumpVersion(): void {
    this.version = (this.version + 1) >>> 0;
  }
}

function clamp(x: number, lo: number, hi: number): number {
  return x < lo ? lo : x > hi ? hi : x;
}

function defaultRng(): () => number {
  let a = 0;
  return function rng(): number {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hexId(rng: () => number): string {
  return Math.floor(rng() * 0xffffffff)
    .toString(16)
    .padStart(8, '0');
}
